import React, { useEffect, useMemo } from 'react';
import { Grid, Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import _ from 'lodash';
import { useFormContext } from 'react-hook-form';
import Select from '../../select/Select';
import FormInput from '../../input/FormInput';
import { basisOptions, componentOptions, getMilkTypeOptionsForBasis } from './select-options';
import ComplexPaymentRate from './ComplexPaymentRate';
import BooleanSwitch from '../../boolean/BooleanSwitch';

const ApplyLabel = styled(Typography)(({ theme }) => ({
    marginBottom: theme.spacing(1),
    fontWeight: 500,
}));

const SwitchContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    width: 300,
    border: '1px solid #e0e0e0',
    borderRadius: theme.spacing(1),
    padding: theme.spacing(1),
    backgroundColor: '#fff',
}));

const OptionLabel = styled(Typography, {
    shouldForwardProp: (prop) => prop !== 'isActive',
})(({ theme, isActive }) => ({
    width: 150,
    fontWeight: isActive ? 600 : 400,
    color: isActive ? theme.palette.text.primary : theme.palette.text.secondary,
}));

const SwitchWrapper = styled(Box)({
    margin: 0,
    '& .MuiFormControl-root': {
        margin: 0,
    },
});

// The form for adding a 'basic' rule for a payment line item rate (i.e. not a 'tiered' rate)
function BasicLineItemRate({ name, inputSizes = 'medium', disabledOnEdit = false }) {
    const { watch, setValue, getValues } = useFormContext();

    const basisWatch = watch(`${name}.basis`);
    const ruleTypeWatch = watch('ruleType');

    // useMemo to prevent multiple re-renders because of new array creation when using filter
    const filteredBasisOptions = useMemo(() => {
        if (ruleTypeWatch === 'haulerVendor') {
            return basisOptions.filter((option) => !['percent_gross_pay', 'percent_net_pay'].includes(option.id));
        }
        return basisOptions;
    }, [ruleTypeWatch]);

    const shouldShowMilkType = () => {
        if (!basisWatch) return false;
        const showValues = ['cwt', 'lbs', 'per_load', 'per_pickup', 'every_other_day_stop_charge', 'over_every_other_day_stop_charge', 'per_day_with_pickup'];
        return showValues.includes(basisWatch);
    };

    const shouldShowComponent = () => {
        if (!basisWatch) return false;
        const showValues = ['cwt', 'lbs'];
        return showValues.includes(basisWatch);
    };

    const shouldShowComponentPounds = () => {
        return ['percent_cwt_milk_value', 'percent_component_milk_value'].includes(basisWatch);
    };

    const shouldShowMinMaxCharges = () => {
        if (!basisWatch) return false;
        return !['fixed', 'percent_gross_pay', 'percent_net_pay', 'percent_cwt_milk_value', 'percent_component_milk_value'].includes(basisWatch);
    };

    const shouldShowApplyPerPickup = () => {
        if (!basisWatch) return false;
        const showValues = ['cwt', 'lbs', 'per_load', 'per_pickup'];
        return showValues.includes(basisWatch);
    };

    useEffect(() => {
        // reset fields as some are not applicable for certain basis values
        if (!shouldShowMilkType()) {
            setValue(`${name}.milkType`, null);
        }
        if (!shouldShowComponent()) {
            setValue(`${name}.component`, null);
        }
        if (!shouldShowComponentPounds()) {
            setValue(`${name}.componentPounds`, null);
        }
        if (!shouldShowMinMaxCharges()) {
            setValue(`${name}.minCharge`, null);
            setValue(`${name}.maxCharge`, null);
        }
        if (!shouldShowApplyPerPickup()) {
            setValue(`${name}.applyPerPickup`, null);
        }
    }, [basisWatch]);

    return (
        <>
            <Grid container spacing={2}>
                <Grid item xs={4}>
                    <BooleanSwitch name={`${name}.complexRate`} props={{ defaultValue: false, label: 'Complex Rate', required: true, disabled: disabledOnEdit }} />
                </Grid>
            </Grid>
            <Grid container spacing={2}>
                {watch(`${name}.complexRate`) ? (
                    <Grid item xs={12}>
                        <ComplexPaymentRate name={name} disabledOnEdit={disabledOnEdit} />
                    </Grid>
                ) : (
                    <Grid item xs={4}>
                        <FormInput name={`${name}.rate`} props={{ size: inputSizes, label: 'Rate', required: true, type: 'number', default: 0, step: 0.1, variant: 'outlined', disabled: disabledOnEdit }} />
                    </Grid>
                )}
            </Grid>
            <Grid container marginTop={watch(`${name}.complexRate`) ? 8 : null}>
                <Grid container spacing={2}>
                    <Grid item xs={4}>
                        <Select name={`${name}.basis`} props={{ size: inputSizes, label: 'Basis', options: filteredBasisOptions, required: true, disabled: disabledOnEdit }} />
                    </Grid>
                    {shouldShowMilkType() && (
                        <Grid item xs={4}>
                            <Select name={`${name}.milkType`} props={{ size: inputSizes, label: 'Milk Type', options: getMilkTypeOptionsForBasis(watch(`${name}.basis`)), required: true, disabled: disabledOnEdit }} />
                        </Grid>
                    )}
                </Grid>
                <Grid container spacing={2}>
                    {shouldShowComponent() && (
                        <Grid item xs={4} sx={{ paddingTop: 0 }}>
                            <Select name={`${name}.component`} props={{ size: inputSizes, label: 'Component', options: componentOptions, required: true, disabled: disabledOnEdit }} />
                        </Grid>
                    )}
                    {shouldShowComponentPounds() && (
                        <Grid item xs={4} sx={{ paddingTop: 0 }}>
                            <FormInput name={`${name}.componentPounds`} props={{ type: 'number', size: inputSizes, label: 'Component Pounds', required: true, variant: 'outlined', disabled: disabledOnEdit }} />
                        </Grid>
                    )}
                </Grid>
                {shouldShowMinMaxCharges() && (
                    <Grid container spacing={2}>
                        <Grid item xs={4}>
                            <FormInput name={`${name}.minCharge`} props={{ size: inputSizes, label: 'Min Charge ($)', type: 'number', default: 0, step: 0.1, variant: 'outlined', disabled: disabledOnEdit }} />
                        </Grid>
                        <Grid item xs={4}>
                            <FormInput name={`${name}.maxCharge`} props={{ size: inputSizes, label: 'Max Charge ($)', type: 'number', default: 0, step: 0.1, variant: 'outlined', disabled: disabledOnEdit }} />
                        </Grid>
                    </Grid>
                )}
                {shouldShowApplyPerPickup() && (
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Box>
                                <ApplyLabel variant="body2">Apply *</ApplyLabel>
                                <SwitchContainer>
                                    <OptionLabel variant="body2" isActive={!watch(`${name}.applyPerPickup`)}>
                                        On Aggregate
                                    </OptionLabel>
                                    <SwitchWrapper>
                                        <BooleanSwitch name={`${name}.applyPerPickup`} props={{ defaultValue: false, disabled: disabledOnEdit }} />
                                    </SwitchWrapper>
                                    <OptionLabel variant="body2" isActive={watch(`${name}.applyPerPickup`)}>
                                        Per Pickup
                                    </OptionLabel>
                                </SwitchContainer>
                            </Box>
                        </Grid>
                    </Grid>
                )}
            </Grid>
        </>
    );
}

export default BasicLineItemRate;
